<script setup lang="ts">
import FinestFieldInput from '@/components/FinestFieldInput.vue'
import type { FieldState } from '@/utils/field-state'
import type { FieldInputType } from '@/utils/field-value'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import PositionSuggestInput from './PositionSuggestInput.vue'
import { formatCell } from './positions'
import type { PositionCellLabels, PositionCellValue, PositionColumn } from './types'

const props = withDefaults(
  defineProps<{
    column: PositionColumn
    value: PositionCellValue
    readonly: boolean
    labels: PositionCellLabels
    maxFractionDigits: number
    state?: FieldState | null
  }>(),
  { state: null },
)

const emit = defineEmits<{
  update: [value: PositionCellValue]
  commit: []
}>()

const { locale } = useI18n()

const displayValue = computed(() =>
  formatCell(props.column, props.value, {
    locale: locale.value,
    labels: props.labels,
    maxFractionDigits: props.maxFractionDigits,
  }),
)

const inputType = computed(() => props.column.type as FieldInputType)
</script>

<template>
  <span v-if="readonly" class="text-ink-2 inline-block px-2.5 py-1 tabular-nums">{{ displayValue }}</span>

  <PositionSuggestInput
    v-else-if="column.type === 'suggest'"
    :label="(value as string) ?? ''"
    :suggestions="column.suggestions ?? []"
    :placeholder="column.placeholder"
    :aria-label="column.label"
    @update="(label) => emit('update', label)" />

  <FinestFieldInput
    v-else
    :model-value="value"
    :type="inputType"
    :label="column.label"
    :options="column.options"
    :placeholder="column.placeholder"
    :suffix="column.suffix"
    :fraction-digits="column.fractionDigits"
    :max-fraction-digits="maxFractionDigits"
    :use-grouping="column.useGrouping"
    :state="state"
    @update:model-value="(next) => emit('update', next)"
    @commit="emit('commit')" />
</template>
